import { useState } from 'react'
import classNames from 'classnames'
import { useFormikContext } from 'formik'

import { useDraggingOver, useDragOverZone } from '../../../../hooks'
import { Avatar, AVATAR_HEIGHT_32 } from '../../../Avatar'
import { useChatroomInputDraft } from '../../Chatroom.hooks'
import {
  getChatroomEditDraft,
  saveChatroomEditDraft,
} from '../../Chatroom.utils'
import {
  useChatroomMessageEditFiles,
  useChatroomMessageEditUnsavedChanges,
} from '../ChatroomMessageEditForm.hooks'
import { getChatroomMessageEditIsSaveDisabled } from '../ChatroomMessageEditForm.utils'
import { ChatroomMessageEditFormActions } from '../ChatroomMessageEditFormActions'
import { ChatroomMessageEditFormEditor } from '../ChatroomMessageEditFormEditor'
import { ChatroomMessageEditFormFiles } from '../ChatroomMessageEditFormFiles'

import * as styles from './ChatroomMessageEditFormFields.module.css'

function ChatroomMessageEditFormFields({
  children,
  files,
  initialFiles,
  isFullScreen,
  isSubmitting,
  message,
  messageDraftRef,
  outboundUuid,
  storageFiles,
  onCancel,
  onCloseModal,
  onSetFiles,
  onSetModal,
  onSetStorageFiles,
}) {
  const { submitForm } = useFormikContext()

  const [isEditorReady, setEditorReady] = useState(false)

  useChatroomInputDraft({
    draftKey: message.uuid,
    files,
    getDraft: getChatroomEditDraft,
    messageDraftRef,
    saveDraft: saveChatroomEditDraft,
    storageFiles,
    onSetFiles,
    onSetStorageFiles,
  })

  const {
    allFiles,
    attachActionOptions,
    filesError,
    filesUploading,
    handleAttachAction,
    handleRemoveFileTag,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
    showFiles,
  } = useChatroomMessageEditFiles({
    files,
    outboundUuid,
    storageFiles,
    onCloseModal,
    onSetFiles,
    onSetModal,
    onSetStorageFiles,
  })

  const isChanged = useChatroomMessageEditUnsavedChanges({
    files: allFiles,
    initialFiles,
  })

  const {
    handleDragOver,
    handleDropFile,
    isDragging,
    ref: draggingRef,
  } = useDraggingOver()

  const {
    attachOver,
    handleDragAttachLeave,
    handleDragAttachOver,
    handleDragInlineLeave,
    handleDragInlineOver,
    inlineOver,
  } = useDragOverZone({ onDragOver: handleDragOver })

  function handleInitEditor() {
    setEditorReady(true)
  }

  function handleClickSubmit() {
    submitForm()
  }

  const isSaveDisabled = getChatroomMessageEditIsSaveDisabled({
    filesUploading,
    isChanged,
    isSubmitting,
  })

  const formClassName = classNames(styles.form, {
    [styles.formLoading]: !isEditorReady,
    [styles.formReady]: isEditorReady,
  })

  return (
    <div className={styles.chatroomMessageEditFormFields}>
      {isEditorReady ? null : children}
      <div className={formClassName} ref={draggingRef}>
        <Avatar
          color={message.owner_obj?.avatar_color}
          size={AVATAR_HEIGHT_32}
          src={message.owner_obj?.avatar}
          text={message.owner_obj?.fullname}
        />
        <div className={styles.body}>
          <ChatroomMessageEditFormEditor
            inlineOver={inlineOver}
            isDragging={isDragging}
            isFullScreen={isFullScreen}
            isSubmitting={isSubmitting}
            onDragInlineLeave={handleDragInlineLeave}
            onDragInlineOver={handleDragInlineOver}
            onDropFile={handleDropFile}
            onInit={handleInitEditor}
          />
          {showFiles ? (
            <ChatroomMessageEditFormFiles
              errorItems={filesError}
              files={allFiles}
              isSubmitting={isSubmitting}
              uploadingItems={filesUploading}
              onRemoveFileTag={handleRemoveFileTag}
            />
          ) : null}
          <ChatroomMessageEditFormActions
            attachActionOptions={attachActionOptions}
            attachOver={attachOver}
            isDragging={isDragging}
            isSaveDisabled={isSaveDisabled}
            isSubmitting={isSubmitting}
            outboundUuid={outboundUuid}
            onAttachAction={handleAttachAction}
            onCancel={onCancel}
            onClickSubmit={handleClickSubmit}
            onDragAttachLeave={handleDragAttachLeave}
            onDragAttachOver={handleDragAttachOver}
            onDropFile={handleDropFile}
            onFinishUpload={handleUploadFile}
            onStartUpload={handleStartUploading}
            onUploadErrors={handleUploadErrors}
          />
        </div>
      </div>
    </div>
  )
}

export default ChatroomMessageEditFormFields
